// Themed Excel + PDF export helpers for dForms, the same look as dAdmin's
// exports (dAssist, dNews, dSpr, dSlip, tickets): navy brand band with
// "Dolluz Corp." and the module name in orange, a slate meta block, a navy
// table header and zebra rows. Copied from dAdmin's src/utils/ticketExport.js;
// the palette and sizes must stay identical.
//
// columns: [{ key, label, width, wrap?, pdfWidth?, align? }]
// rows:    [{ <key>: value, ... }]
//
// Two dForms additions to the PDF, both optional:
//   repeatKeys:  when there are more than PDF_COLUMNS_PER_PAGE other columns,
//                the table continues on the next page with these columns
//                repeated first (e.g. Response ID), so each page stays readable.
//   cellImage:   (rowIndex, key) => image data URL or null; drawn at the
//                bottom of that body cell (signatures).

import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addEagleWatermark } from "./pdfWatermark";

// Palette - identical hex values across Excel + PDF.
const COL = {
    navy:   { argb: "FF0D1B2A", rgb: [13, 27, 42] },
    orange: { argb: "FFE8520A", rgb: [232, 82, 10] },
    sub:    { argb: "FFAABBCC", rgb: [170, 187, 204] },
    slate:  { argb: "FF64748B", rgb: [100, 116, 139] },
    line:   { argb: "FFE2E8F0", rgb: [226, 232, 240] },
    zebra:  { argb: "FFFAFBFD", rgb: [250, 251, 253] },
};

export const PDF_COLUMNS_PER_PAGE = 10;

function fmtTs(d) {
    if (!d) return "";
    try {
        const x = new Date(d);
        return x.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
    } catch { return String(d); }
}

// ---- EXCEL -----------------------------------------------------------
export async function exportResponsesExcel({
    moduleName,       // "dForms"
    sheetTitle,       // e.g. Responses for "Training application"
    columns,
    rows,
    filterLabel,
    filename,
}) {
    const wb = new ExcelJS.Workbook();
    wb.creator = `Dolluz Corp · ${moduleName}`;
    wb.created = new Date();
    // Sheet names: at most 31 characters, none of \ / ? * [ ] :
    const ws = wb.addWorksheet(String(sheetTitle).replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Responses");
    const cols = Math.max(columns.length, 2);

    ws.columns = columns.map(c => ({ key: c.key, width: c.width || 20 }));

    // Navy header band ("Dolluz Corp." + orange dot + "  moduleName")
    const brandRow = ws.addRow([]);
    ws.mergeCells(brandRow.number, 1, brandRow.number, cols);
    const brandCell = brandRow.getCell(1);
    brandCell.value = {
        richText: [
            { text: "Dolluz Corp",       font: { bold: true, color: { argb: "FFFFFFFF" }, size: 16, name: "Calibri" } },
            { text: ".",                 font: { bold: true, color: { argb: COL.orange.argb }, size: 16, name: "Calibri" } },
            { text: `    ${moduleName}`, font: { bold: true, color: { argb: COL.orange.argb }, size: 16, name: "Calibri" } },
        ],
    };
    brandCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
    brandCell.alignment = { vertical: "middle", indent: 1 };
    brandRow.height = 28;

    // Sub-band under the brand
    const subRow = ws.addRow([sheetTitle]);
    ws.mergeCells(subRow.number, 1, subRow.number, cols);
    const subCell = subRow.getCell(1);
    subCell.font = { color: { argb: COL.sub.argb }, size: 10, name: "Calibri" };
    subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
    subCell.alignment = { vertical: "middle", indent: 1 };
    subRow.height = 18;

    ws.addRow([]);

    // Meta block - filter (if any), generated timestamp, row count
    const metaRows = [
        ...(filterLabel ? [["Filter:",    filterLabel]] : []),
        ["Generated:", fmtTs(new Date().toISOString()) + " IST"],
        ["Rows:",      String(rows.length)],
    ];
    metaRows.forEach(([k, v]) => {
        const row = ws.addRow([k, v]);
        row.getCell(1).font = { color: { argb: COL.slate.argb }, size: 10 };
        row.getCell(2).font = { bold: true, color: { argb: COL.navy.argb }, size: 10 };
    });
    ws.addRow([]);

    // Table header
    const hdrRow = ws.addRow(columns.map(c => c.label));
    hdrRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10.5, name: "Calibri" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
        cell.alignment = { vertical: "middle", indent: 1 };
        cell.border = { bottom: { style: "thin", color: { argb: COL.line.argb } } };
    });
    hdrRow.height = 22;

    // Body
    rows.forEach((r, i) => {
        const row = ws.addRow(r);
        row.eachCell((cell, col) => {
            cell.font = { color: { argb: COL.navy.argb }, size: 10, name: "Calibri" };
            if (i % 2 === 1) {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.zebra.argb } };
            }
            cell.alignment = { vertical: "top", wrapText: !!columns[col - 1]?.wrap };
            cell.border = { bottom: { style: "hair", color: { argb: COL.line.argb } } };
        });
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ---- PDF -------------------------------------------------------------
export function exportResponsesPdf({
    moduleName,
    sheetTitle,
    columns,
    rows,
    filterLabel,
    filename,
    repeatKeys = [],
    cellImage = null,
}) {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Navy header band
    doc.setFillColor(...COL.navy.rgb);
    doc.rect(0, 0, pageWidth, 60, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text("Dolluz Corp", 28, 32);
    const wDol = doc.getTextWidth("Dolluz Corp");
    doc.setTextColor(...COL.orange.rgb);
    doc.text(".", 28 + wDol, 32);
    const wDot = doc.getTextWidth(".");
    doc.text(`    ${moduleName}`, 28 + wDol + wDot, 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COL.sub.rgb);
    doc.text(sheetTitle, 28, 50);

    // Meta lines
    let metaY = 86;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (filterLabel) {
        doc.setTextColor(...COL.slate.rgb);
        doc.text("Filter:", 28, metaY);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COL.navy.rgb);
        doc.text(filterLabel, 70, metaY);
        doc.setFont("helvetica", "normal");
        metaY += 14;
    }
    doc.setTextColor(...COL.slate.rgb);
    doc.text("Generated:", 28, metaY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COL.navy.rgb);
    doc.text(fmtTs(new Date().toISOString()) + " IST", 90, metaY);
    metaY += 14;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COL.slate.rgb);
    doc.text("Rows:", 28, metaY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COL.navy.rgb);
    doc.text(String(rows.length), 70, metaY);

    // Many questions: one table per group of columns, each on its own page,
    // with the repeatKeys columns first on every page. A page takes columns
    // while their minimum widths fit (with room for wrapped text to grow),
    // and at most PDF_COLUMNS_PER_PAGE of them.
    const fixed = columns.filter(c => repeatKeys.includes(c.key));
    const rest = columns.filter(c => !repeatKeys.includes(c.key));
    const minW = (c) => Math.max(c.pdfWidth || 0, c.wrap ? 110 : 60);
    const budget = (pageWidth - 56) * 0.9 - fixed.reduce((sum, c) => sum + minW(c), 0);
    const groups = [];
    let current = [];
    let used = 0;
    rest.forEach(c => {
        if (current.length && (used + minW(c) > budget || current.length >= PDF_COLUMNS_PER_PAGE)) {
            groups.push([...fixed, ...current]);
            current = [];
            used = 0;
        }
        current.push(c);
        used += minW(c);
    });
    if (current.length) groups.push([...fixed, ...current]);
    if (!groups.length) groups.push(fixed.length ? fixed : columns);

    // Table. Fill the full page width and let columns auto-size to at least
    // their header/content width, so single-word headers never wrap/cut in
    // half. pdfWidth is used only as a minimum hint - columns grow beyond it
    // to fill the page.
    const tableWidth = pageWidth - 56;   // 28pt margin each side
    groups.forEach((group, groupIndex) => {
        if (groupIndex > 0) doc.addPage();
        const imageAt = (data) => (cellImage && data.section === "body"
            ? cellImage(data.row.index, group[data.column.index]?.key)
            : null);
        autoTable(doc, {
            startY: groupIndex === 0 ? metaY + 12 : 28,
            margin: { left: 28, right: 28, top: 28 },
            tableWidth,
            head: [group.map(c => c.label)],
            body: rows.map(r => group.map(c => (r[c.key] == null ? "" : String(r[c.key])))),
            theme: "plain",
            styles: {
                font: "helvetica",
                fontSize: 9.5,
                cellPadding: { top: 7, right: 9, bottom: 7, left: 9 },
                textColor: COL.navy.rgb,
                lineColor: COL.line.rgb,
                lineWidth: 0.4,
                valign: "middle",
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: COL.navy.rgb,
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 10.5,
                halign: "left",
                valign: "middle",
                cellPadding: { top: 9, right: 9, bottom: 9, left: 9 },
                lineWidth: 0,
            },
            bodyStyles: { minCellHeight: 20 },
            alternateRowStyles: { fillColor: COL.zebra.rgb },
            columnStyles: group.reduce((acc, c, i) => {
                const st = {};
                if (c.pdfWidth) st.minCellWidth = c.pdfWidth;   // minimum, not a hard cap
                if (c.align) st.halign = c.align;
                if (Object.keys(st).length) acc[i] = st;
                return acc;
            }, {}),
            // Room under the text for a signature image.
            didParseCell(data) {
                if (imageAt(data)) data.cell.styles.minCellHeight = 44;
            },
            didDrawCell(data) {
                const img = imageAt(data);
                if (!img) return;
                const w = Math.min(data.cell.width - 18, 90);
                const h = w / 3;
                doc.addImage(img, "PNG", data.cell.x + 9, data.cell.y + data.cell.height - h - 5, w, h);
            },
        });
    });

    addEagleWatermark(doc);
    doc.save(filename);
}
